import { useMemo } from "react"
import styled from "styled-components"

import { spacing, Text, TextField } from "@minuthq/meatball-ui-react"

import { ParadiseNativeDateField } from "src/components/Paradise/NativeDateTimeField"
import { ParadiseCountrySelector } from "src/components/Paradise/ParadiseCountrySelector"
import { ParadiseCreateQuoteDialogSuccess } from "src/components/Paradise/ParadiseQuotes/ParadiseCreateQuoteDialog/ParadiseCreateQuoteDialogSuccess"
import { ParadiseCreateQuoteNoteField } from "src/components/Paradise/ParadiseQuotes/ParadiseCreateQuoteDialog/ParadiseCreateQuoteNoteField"
import { useParadiseCreateQuote } from "src/components/Paradise/ParadiseQuotes/ParadiseCreateQuoteDialog/useParadiseCreateQuote"
import {
  initialParadiseCreateQuoteValues,
  useParadiseQuoteForm,
} from "src/components/Paradise/ParadiseQuotes/ParadiseCreateQuoteDialog/useParadiseQuoteForm"
import { ParadiseShippableProductsQuantityTable } from "src/components/Paradise/ParadiseShippableProductsQuantityTable"
import { ParadiseUserCombobox } from "src/components/Paradise/ParadiseUserCombobox"
import { useFlags } from "src/hooks/useFlags"
import { Combobox } from "src/ui/Combobox/Combobox"
import { MDialog } from "src/ui/Dialog/MDialog"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { capitalizeFirstChar } from "src/utils/l10n"

const FORM_ID = "paradise-create-quote-form"

export function ParadiseCreateQuoteDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { userBasedQuotes } = useFlags()

  const {
    form,
    createQuoteError,
    isCreatingQuote,
    createQuoteData,
    resetCreateQuote,
  } = useParadiseQuoteForm()

  const {
    shippingDestinations,
    isLoadingShippingDestinations,
    products,
    isInitialLoadingProducts,
    selectedProduct,
    selectedPlanAssociatedShippableProducts,
    selectedAdditionalShippableProducts,
    selectedUser,
    selectedUserCurrentSubscription,
    selectedUserBillingInfo,
    fetchError,
  } = useParadiseCreateQuote({
    userId: form.values.userId,
    shippingCountry: form.values.shippingCountry,
    planId: form.values.cart.planId,
    onChangeUserSuccess: ({ user, billingInfo, currentSubscription }) => {
      form.setValues({
        userId: user.id,
        shippingCountry:
          billingInfo?.billing_address?.country ?? form.values.shippingCountry,
        cart: {
          ...form.values.cart,
          planId: currentSubscription?.plan_id ?? form.values.cart.planId,
          additionalShippableProducts: [],
          planAssociatedShippableProducts: [],
        },
      })
    },
  })

  const error = useMemo(() => {
    if (createQuoteError) {
      return {
        title: createQuoteError.message,
        message: "Failed to create quote",
      }
    }

    if (fetchError) {
      return fetchError
    }

    if (selectedUser?.subscription_status === "cancelled") {
      return {
        title: "User has cancelled their subscription",
        message: "Please select a different user",
      }
    }

    if (selectedUser?.subscription_status === "non_renewing") {
      return {
        title: "User has a non-renewing subscription",
        message: "Please select a different user",
      }
    }

    return null
  }, [createQuoteError, fetchError, selectedUser])

  function getDialogContent() {
    if (isLoadingShippingDestinations) {
      return (
        <div>
          <MSkeleton height={50} />
          <MSkeleton height={40} width="30ch" />
          <MSkeleton height={40} width="30ch" />
        </div>
      )
    }

    if (createQuoteData) {
      return (
        <SuccessWrapper>
          <ParadiseCreateQuoteDialogSuccess
            quoteId={createQuoteData.id}
            shippingCountry={form.values.shippingCountry}
          />
        </SuccessWrapper>
      )
    }

    return (
      <Form onSubmit={form.handleSubmit} id={FORM_ID}>
        {userBasedQuotes && (
          <FieldSet>
            <Text variant="subtitle">User</Text>
            <ParadiseUserCombobox
              selectedUser={selectedUser || null}
              onChange={(value) => {
                form.setValues({
                  userId: value.user_id,
                })
              }}
              onClear={() => {
                form.setValues({
                  userId: null,
                })
              }}
            />
          </FieldSet>
        )}

        <FieldSet>
          <Text variant="subtitle">General</Text>
          <TextField
            label="Customer name"
            value={form.values.customerName}
            onChange={(value) => {
              form.setValues({
                customerName: value,
              })
            }}
            name="customer-name"
            required
            requiredIndicator
          />

          <TextField
            label="Discount code"
            value={form.values.cart.discountCode}
            onChange={(value) => {
              form.setValues({
                cart: {
                  ...form.values.cart,
                  discountCode: value,
                },
              })
            }}
            name="discount-code"
          />

          <ParadiseCountrySelector
            label="Shipping country"
            name="shipping-country"
            countryCodes={
              shippingDestinations?.map(
                (destination) => destination.country_code
              ) || []
            }
            selectedCountryCode={form.values.shippingCountry}
            onSelectCountryCode={(value) => {
              form.setValues({
                shippingCountry: value,
                cart: {
                  ...initialParadiseCreateQuoteValues.cart,
                  discountCode: form.values.cart.discountCode,
                },
              })
            }}
            disabled={!!selectedUserBillingInfo?.billing_address}
            required
            requiredIndicator
          />

          <ParadiseNativeDateField
            label="Expires at"
            value={form.values.expiresAt}
            onChange={(value) => {
              form.setValues({
                expiresAt: value,
              })
            }}
            required
            requiredIndicator
            min={new Date()}
          />
        </FieldSet>

        <FieldSet>
          <Text variant="subtitle">Customer note</Text>
          <Text>This note will be visible in the checkout</Text>
          <ParadiseCreateQuoteNoteField
            label="Customer note"
            markdownValue={form.values.customerNote}
            onChange={(value) => {
              form.setValues({
                customerNote: value,
              })
            }}
            required
            requiredIndicator
          />
        </FieldSet>

        <FieldSet
          disabled={!form.values.shippingCountry || isInitialLoadingProducts}
        >
          <Text variant="subtitle">Plan</Text>
          <PlanWrapper>
            <Combobox
              label="Plan"
              input={form.values.cart.planId}
              onInput={() => {}}
              selectedValue={form.values.cart.planId}
              onChange={(value) => {
                form.setValues({
                  cart: {
                    ...form.values.cart,
                    planId: value,
                  },
                })
              }}
              options={
                products?.map((product) => ({
                  label: `${capitalizeFirstChar(product.plan).replace("_", " ")} (${product.plan_id})`,
                  value: product.plan_id,
                })) || []
              }
              required
              requiredIndicator
              disabled={!!selectedUserCurrentSubscription}
            />
            <TextField
              label="Quantity"
              type="number"
              min={1}
              value={form.values.cart.prepaidHomeQuantity.toString()}
              onChange={(value) => {
                form.setValues({
                  cart: {
                    ...form.values.cart,
                    prepaidHomeQuantity: parseInt(value),
                    planAssociatedShippableProducts:
                      selectedPlanAssociatedShippableProducts?.map(
                        (product) => ({
                          productId: product.id,
                          quantity: parseInt(value),
                        })
                      ) || [],
                  },
                })
              }}
              name="quantity"
              disabled={!selectedProduct}
              required
              requiredIndicator
            />
          </PlanWrapper>
        </FieldSet>

        <FieldSet disabled={!selectedProduct}>
          <Text variant="subtitle">Plan associated shippable products</Text>
          <ParadiseShippableProductsQuantityTable
            availableShippableProducts={
              selectedPlanAssociatedShippableProducts?.map((product) => ({
                productId: product.id,
                imageUrl: product.image_url,
              })) || []
            }
            selectedShippableProducts={
              form.values.cart.planAssociatedShippableProducts
            }
            updateShippableProducts={(products) => {
              form.setValues({
                cart: {
                  ...form.values.cart,
                  planAssociatedShippableProducts: products,
                },
              })
            }}
            currency="USD"
            hideTotal
            disableQuantity
          />
        </FieldSet>

        <FieldSet disabled={!selectedProduct}>
          <Text variant="subtitle">Additional shippable products</Text>
          <ParadiseShippableProductsQuantityTable
            availableShippableProducts={
              selectedAdditionalShippableProducts?.map((product) => ({
                productId: product.id,
                imageUrl: product.image_url,
              })) || []
            }
            selectedShippableProducts={
              form.values.cart.additionalShippableProducts
            }
            updateShippableProducts={(products) => {
              form.setValues({
                cart: {
                  ...form.values.cart,
                  additionalShippableProducts: products,
                },
              })
            }}
            currency="USD"
            hideTotal
          />
        </FieldSet>
      </Form>
    )
  }

  return (
    <MDialog
      title="Create quote"
      open={open}
      onClose={() => {
        resetCreateQuote()
        form.resetForm()
        onClose()
      }}
      width="large"
      confirmLabel="Create"
      confirmButtonProps={{
        loading: isCreatingQuote,
        disabled:
          !!fetchError ||
          selectedUser?.subscription_status === "cancelled" ||
          selectedUser?.subscription_status === "non_renewing",
      }}
      formId={!createQuoteData ? FORM_ID : undefined}
      closeLabel={createQuoteData ? "Close" : "Cancel"}
      error={
        !!error && (
          <div>
            <Text variant="subtitle" color="unset">
              {error.title}
            </Text>
            <Text color="unset">{error.errorKey}</Text>
            <Text color="unset">{error.message}</Text>
          </div>
        )
      }
    >
      <div>{getDialogContent()}</div>
    </MDialog>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
`

const FieldSet = styled.fieldset`
  all: unset;

  display: flex;
  flex-direction: column;
  gap: ${spacing.M};

  transition: opacity 0.3s;

  &:disabled {
    opacity: 0.5;
  }
`

const PlanWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${spacing.M};
`

const SuccessWrapper = styled.div`
  height: 300px;
`
